import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faEnvelope, 
  faBriefcase, 
  faClock,
  faTicketAlt,
  faCopy,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useState } from 'react';

const SuccessNotification = ({ email, trackingId, onClose }) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(trackingId).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ y: 50 }}
        animate={{ y: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-8 text-center max-h-[90vh] overflow-y-auto"
      >
        {/* Success Icon */}
        <div className="mb-5">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <FontAwesomeIcon 
              icon={faCheckCircle} 
              className="text-3xl sm:text-4xl text-green-600" 
            />
          </div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800 mb-2">
            {t('incident_report.success.title', 'Report Submitted Successfully!')}
          </h2>
        </div>

        {/* Tracking ID */}
        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mb-5">
          <div className="flex items-center justify-center mb-2">
            <FontAwesomeIcon icon={faTicketAlt} className="text-blue-600 mr-2" />
            <span className="font-medium text-blue-800 text-sm sm:text-base">
              {t('incident_report.success.tracking_id', 'Your Tracking ID')}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-blue-700">
            {t('incident_report.success.save_tracking_id', 'DO NOT LOSE this tracking ID, Save this tracking ID to check your report status:')}
          </p>
          <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
            <p className="font-semibold text-blue-800 text-base sm:text-lg break-all">{trackingId}</p>
            <button onClick={handleCopy} className="p-2 rounded-md bg-blue-100 hover:bg-blue-200 transition-colors flex-shrink-0">
              <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} className={isCopied ? "text-green-600" : "text-blue-600"} />
            </button>
          </div>
        </div>

        {/* Email Confirmation (only if email provided) */}
        {email && (
          <div className="bg-green-50 rounded-lg p-3 sm:p-4 mb-5">
            <div className="flex items-center justify-center mb-2">
              <FontAwesomeIcon icon={faEnvelope} className="text-green-600 mr-2" />
              <span className="font-medium text-green-800 text-sm sm:text-base">
                {t('incident_report.success.email_sent', 'Email Confirmation Sent')}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-green-700">
              {t('incident_report.success.email_message', 'We have sent confirmation details to:')}
            </p>
            <p className="font-semibold text-green-800 mt-1 break-all text-sm sm:text-base">{email}</p>
          </div>
        )}

        {/* Next Steps */}
        <div className="space-y-3 mb-5">
          <div className="flex items-start text-left">
            <FontAwesomeIcon icon={faBriefcase} className="text-primary mt-1 mr-3 flex-shrink-0" />
            <p className="text-xs sm:text-sm text-gray-600">
              {t('incident_report.success.team_message', 'Our team will work on your report and keep you updated.')}
            </p>
          </div>
          <div className="flex items-start text-left">
            <FontAwesomeIcon icon={faClock} className="text-primary mt-1 mr-3 flex-shrink-0" />
            <p className="text-xs sm:text-sm text-gray-600">
              {!email 
                ? t('incident_report.success.use_tracking_id', 'Use your tracking ID above to check the status of your report.')
                : t('incident_report.success.check_email', 'Please check your email for tracking information and next steps.')}
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="bg-primary text-white w-full sm:w-auto px-8 py-3 rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            {t('incident_report.success.ok', 'OK')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

SuccessNotification.propTypes = {
  email: PropTypes.string,
  trackingId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default SuccessNotification;